import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const LoginPage = () => {
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [view, setView] = useState('main'); // 'main' | 'signup' | 'inbox'
  const [authError, setAuthError] = useState('');
  const googleBtnRef = useRef(null);

  // Initialise Google Identity Services and render the official Sign-In button.
  // GIS calls handleCredential with a verified id_token, which we exchange for our
  // own session token via the backend.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return; // feature off until the client id is configured

    const handleCredential = async (resp) => {
      try {
        setAuthError('');
        await loginWithGoogle(resp.credential);
        navigate('/dashboard');
      } catch (e) {
        setAuthError('Google sign-in failed. Please try again.');
      }
    };

    const render = () => {
      if (!window.google?.accounts?.id) return false;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredential,
      });
      if (googleBtnRef.current) {
        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline',
          size: 'large',
          width: 200,
          text: 'continue_with',
        });
      }
      return true;
    };

    if (render()) return;
    // GIS script may not be loaded yet — poll briefly.
    const iv = setInterval(() => {
      if (render()) clearInterval(iv);
    }, 200);
    const stop = setTimeout(() => clearInterval(iv), 5000);
    return () => {
      clearInterval(iv);
      clearTimeout(stop);
    };
  }, [view, loginWithGoogle, navigate]);

  // All auth actions: show "Check your inbox" screen, then redirect to actual auth
  // Email + SSO are not wired to a real backend yet (no email-OTP infra; no enterprise
  // IdP). They used to redirect to the Emergent template's mock auth (emergentagent.com) —
  // removed. Google Sign-In below is the real, working dashboard login.
  const handleEmailLogin = (e) => {
    e.preventDefault();
    setAuthError('Email sign-in is coming soon — please continue with Google below.');
  };

  const handleSignUpSubmit = (e) => {
    e.preventDefault();
    setAuthError('Email sign-up is coming soon — please continue with Google below.');
  };

  const handleGotIt = () => {
    setView('main');
  };

  const handleSSOLogin = () => {
    setAuthError('SSO is coming soon — please continue with Google.');
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative" style={{ background: '#F9FBF5' }}>
      <div className="w-full max-w-[480px] mx-4">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 px-8 sm:px-12 py-10 sm:py-12">

          {/* ===== MAIN LOGIN VIEW ===== */}
          {view === 'main' && (
            <>
              {/* Logo */}
              <div className="flex justify-center mb-6">
                <div className="h-[52px] w-[52px] rounded-xl flex items-center justify-center bg-[#082900]">
                  <span className="text-white font-bold text-3xl">M</span>
                </div>
              </div>

              <h1 className="text-center text-[22px] font-semibold text-gray-900 mb-8">
                Continue to Duty AI
              </h1>

              <form onSubmit={handleEmailLogin}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-800 mb-2">Email address</label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#0A3900] focus:ring-2 focus:ring-[#0A3900]/20 outline-none transition-all"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full rounded-lg bg-[#0A3900] px-4 py-3 text-sm font-medium text-white hover:bg-[#1A5C0A] transition-colors mb-3"
                >
                  Login
                </button>
              </form>

              <button
                onClick={() => setView('signup')}
                className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50 transition-colors"
              >
                Sign Up
              </button>

              {/* Divider */}
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-sm text-gray-400 whitespace-nowrap">Or continue with</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              {authError && (
                <p className="text-center text-sm text-red-600 mb-3">{authError}</p>
              )}

              {/* Real Google Sign-In (GIS renders the official button here). */}
              <div className="flex justify-center mb-3">
                {GOOGLE_CLIENT_ID ? (
                  <div ref={googleBtnRef} />
                ) : (
                  <span className="text-xs text-gray-400">Google sign-in not configured</span>
                )}
              </div>

              {/* SSO — placeholder until an enterprise IdP is wired */}
              <button
                onClick={handleSSOLogin}
                className="w-full flex items-center justify-center gap-2.5 rounded-lg border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2a7.2 7.2 0 01-6-3.22c.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08a7.2 7.2 0 01-6 3.22z" fill="#333"/>
                </svg>
                SSO
              </button>
            </>
          )}

          {/* ===== SIGN UP VIEW ===== */}
          {view === 'signup' && (
            <>
              {/* Logo */}
              <div className="flex justify-center mb-6">
                <div className="h-[52px] w-[52px] rounded-xl flex items-center justify-center bg-[#082900]">
                  <span className="text-white font-bold text-3xl">M</span>
                </div>
              </div>

              <h1 className="text-center text-[22px] font-semibold text-gray-900 mb-8">
                Create your account
              </h1>

              <form onSubmit={handleSignUpSubmit}>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-800 mb-2">Full name</label>
                  <input
                    type="text"
                    placeholder="John Doe"
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#0A3900] focus:ring-2 focus:ring-[#0A3900]/20 outline-none transition-all"
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-800 mb-2">Email address</label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#0A3900] focus:ring-2 focus:ring-[#0A3900]/20 outline-none transition-all"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full rounded-lg bg-[#0A3900] px-4 py-3 text-sm font-medium text-white hover:bg-[#1A5C0A] transition-colors mt-2 mb-3"
                >
                  Sign Up
                </button>
              </form>

              <button
                onClick={() => setView('main')}
                className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50 transition-colors"
              >
                Back to Login
              </button>

              {/* Divider */}
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-sm text-gray-400 whitespace-nowrap">Or continue with</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              {authError && (
                <p className="text-center text-sm text-red-600 mb-3">{authError}</p>
              )}

              {/* Real Google Sign-In (GIS renders the official button here). */}
              <div className="flex justify-center mb-3">
                {GOOGLE_CLIENT_ID ? (
                  <div ref={googleBtnRef} />
                ) : (
                  <span className="text-xs text-gray-400">Google sign-in not configured</span>
                )}
              </div>

              {/* SSO — placeholder until an enterprise IdP is wired */}
              <button
                onClick={handleSSOLogin}
                className="w-full flex items-center justify-center gap-2.5 rounded-lg border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2a7.2 7.2 0 01-6-3.22c.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08a7.2 7.2 0 01-6 3.22z" fill="#333"/>
                </svg>
                SSO
              </button>
            </>
          )}

          {/* ===== CHECK INBOX VIEW ===== */}
          {view === 'inbox' && (
            <>
              <div className="flex justify-center mb-6">
                <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                </div>
              </div>

              <h1 className="text-center text-[22px] font-semibold text-gray-900 mb-3">
                Check your inbox
              </h1>

              <p className="text-center text-gray-500 mb-8 text-sm leading-relaxed">
                We've sent you an activation link. Please check your email and login with magic link.
              </p>

              <button
                onClick={handleGotIt}
                className="w-full rounded-lg bg-[#0A3900] px-4 py-3 text-sm font-medium text-white hover:bg-[#1A5C0A] transition-colors"
              >
                Got it
              </button>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <p className="mt-8 text-sm text-gray-500">
        By signing in, you agree to our{' '}
        <button onClick={() => navigate('/terms')} className="text-gray-700 underline underline-offset-2 hover:text-[#0A3900] transition-colors">
          Worker Terms of Service
        </button>
        .
      </p>
    </div>
  );
};

export default LoginPage;
